import { gql } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import sha from 'sha.js';
import classNames from 'classnames';
import { Button, Input } from '@moda/om';
import SocialPhoneIcon from '@moda/icons/social-phone-16';
import { hrefFor } from '../../routers';
import { EmailPumoModuleFormFragment } from '../../generated/types';
import { Link } from '../Link';
import { NormalizePumoStyles, sanitizeHTML } from '../NormalizePumoStyles';
import { PhoneNumberTermsAndConditions } from '../PhoneNumberTermsAndConditions';
import { useSubscribe } from '../../hooks/useSubscribe';

import './EmailPumoModuleForm.scss';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  emailUnitForm: EmailPumoModuleFormFragment;
}

enum Step {
  Email = 'email',
  Sms = 'sms',
  Confirmation = 'confirmation'
}

export const EMAIL_PUMO_MODULE_FORM_FRAGMENT = gql`
  fragment EmailPumoModuleFormFragment on EmailUnit {
    ctaText
    useDivider
    genderType
    eyebrowText {
      body
    }
    headlineText {
      body
    }
    taglineText {
      body
    }
  }
`;

export const EmailPumoModuleForm: React.FC<Props> = ({
  className,
  emailUnitForm: { eyebrowText, headlineText, taglineText, ctaText, useDivider }
}) => {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<Step>(Step.Email);
  const {
    subscribe,
    isSubscribed,
    isExistingSubscription,
    error,
    phone,
    setPhone,
    isEmailValid,
    isPhoneNumberValid,
    phoneNumberEnabled,
    mailValidationError,
    phoneNumberValidationError
  } = useSubscribe();

  const isEmailStep = step === Step.Email;
  const isSmsStep = step === Step.Sms;
  const isConfirmationStep = step === Step.Confirmation;

  const handleEmailSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      if (!isEmailValid(email)) return;

      const didSubscribe = await subscribe({ email, label: 'emailPumoModule' });

      if (!didSubscribe) return;

      if (phoneNumberEnabled) {
        setStep(Step.Sms);
      }
    },
    [email, isEmailValid, phoneNumberEnabled, subscribe]
  );

  const handleSmsSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      if (!isPhoneNumberValid(phone)) return;

      const didSubscribe = await subscribe({ email, phone, label: 'emailPumoModule' });

      if (!didSubscribe) return;

      setStep(Step.Confirmation);
    },
    [email, isPhoneNumberValid, phone, subscribe]
  );

  return (
    <div className={classNames('EmailPumoModuleForm', className)}>
      <div className="EmailPumoModuleForm__text">
        {eyebrowText?.body && (
          <div className="EmailPumoModuleForm__eyebrow">{eyebrowText.body}</div>
        )}

        {headlineText?.body && (
          <div className="EmailPumoModuleForm__headline">{headlineText.body}</div>
        )}

        {useDivider && <hr className="EmailPumoModuleForm__divider" />}

        {taglineText?.body && taglineText?.body !== '' && (
          <NormalizePumoStyles
            className="EmailPumoModuleForm__tagline"
            dangerouslySetInnerHTML={{ __html: `${sanitizeHTML(taglineText.body)}` }}
          />
        )}

        {ctaText && <span className="EmailPumoModuleForm__cta">{ctaText}</span>}
      </div>

      {isEmailStep && (
        <form className="EmailPumoModuleForm__form" onSubmit={handleEmailSubmit}>
          <Input
            className="EmailPumoModuleForm__input"
            disabled={isSubscribed}
            error={mailValidationError}
            onChange={setEmail}
            placeholder={isSubscribed ? 'Email submitted!' : 'Sign up for our newsletter'}
            required
            type="email"
            value={isSubscribed ? '' : email}
          />

          <Button disabled={isSubscribed} type="submit">
            Submit
          </Button>

          {error && (
            <div className="EmailPumoModuleForm__error">Unable to subscribe you at this time.</div>
          )}

          <footer className="EmailPumoModuleForm__opt-out">
            To opt out,{' '}
            <Link className="EmailPumoModuleForm__opt-out-link" to={hrefFor.UnsubscribePage()}>
              unsubscribe here
            </Link>{' '}
            or in our emails.
          </footer>
        </form>
      )}

      {isSmsStep && (
        <form className="EmailPumoModuleForm__form" onSubmit={handleSmsSubmit}>
          <Input
            className="EmailPumoModuleForm__input"
            error={phoneNumberValidationError}
            onChange={setPhone}
            placeholder="Mobile Phone"
            required
            type="phone"
            value={phone}
          />

          <PhoneNumberTermsAndConditions
            className="EmailPumoModuleForm__termsconditions"
            color="coal"
          />

          <div className="EmailPumoModuleForm__postnote">U.S. Mobile Number Only</div>

          <Button type="submit">Sign up for texts</Button>

          {error && (
            <div className="EmailPumoModuleForm__error">Unable to subscribe you at this time.</div>
          )}

          <footer className="EmailPumoModuleForm__opt-out">
            To opt out of text messages, reply STOP.
          </footer>
        </form>
      )}

      {isConfirmationStep && (
        <div className="EmailPumoModuleForm__confirmation">
          <SocialPhoneIcon
            color="goldenrod"
            width="36px"
            height="36px"
            className="EmailPumoModuleForm__icon"
          />

          <div className="EmailPumoModuleForm__confirmation-title">Check your texts</div>

          <div className="EmailPumoModuleForm__confirmation-copy">
            Reply “Y” to confirm your subscription. We look forward to sharing early access on
            products, exclusive offers, and more.
          </div>
        </div>
      )}

      {isExistingSubscription && isEmailStep && (
        <div className="EmailPumoModuleForm__already-subscribed">
          You're already on our newsletter list,{' '}
          <Link
            to={`${hrefFor.UnsubscribePage()}?email_hash=${sha('sha256').update(email).digest('hex')}`}
          >
            click here
          </Link>{' '}
          to manage your subscriptions.
        </div>
      )}
    </div>
  );
};
